/// Shared rules for the careers form's proof-of-work links. Both links are required: applications
/// were arriving with a name and an email and nothing else, which gave the team nothing to judge.
/// The form checks these before sending and /api/careers/apply checks them again, so a request
/// that skips the form still cannot land an empty application.

export const LINKEDIN_LABEL = "LinkedIn";
export const CV_LABEL = "CV / Portfolio";

/** Accepts "linkedin.com/in/x" as well as a full URL, since people paste either. */
export function normaliseUrl(raw: string): URL | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    if (!url.hostname.includes(".")) return null;
    return url;
  } catch {
    return null;
  }
}

export function isLinkedInUrl(raw: string): boolean {
  const url = normaliseUrl(raw);
  if (!url) return false;
  const host = url.hostname.toLowerCase();
  return (
    (host === "linkedin.com" || host.endsWith(".linkedin.com")) &&
    url.pathname.replace(/\/+$/, "").length > 1
  );
}

/** Returns a message for the applicant, or null when both links are fine. */
export function proofLinksError(linkedin: string, cv: string): string | null {
  if (!linkedin.trim() && !cv.trim()) {
    return "Please add your LinkedIn and a link to your CV or portfolio, so we can see your experience.";
  }
  if (!linkedin.trim()) return "Please add your LinkedIn profile link.";
  if (!isLinkedInUrl(linkedin)) {
    return "That LinkedIn link does not look right. Copy the address of your profile page, like linkedin.com/in/your-name.";
  }
  if (!cv.trim()) {
    return "Please add a link to your CV or portfolio. A Google Drive or Dropbox link to your CV is fine.";
  }
  if (!normaliseUrl(cv)) {
    return "That CV or portfolio link does not look right. Paste the full web address.";
  }
  return null;
}

/** A couple of real sentences. Long enough that "hi, interested" fails, short enough that nobody
 * with something to say is blocked. */
export const MIN_MESSAGE_CHARS = 60;

export function messageError(message: string): string | null {
  const length = message.trim().length;
  if (length === 0) {
    return "Please tell us a bit about you: what you have done and what you would bring.";
  }
  if (length < MIN_MESSAGE_CHARS) {
    return "Please write a little more about you. A few sentences on what you have done is enough.";
  }
  return null;
}
